(function () {
  'use strict';

  const ROOT_SELECTOR = '[data-rdg-fsb]';
  const GEO_ENDPOINT  = 'https://apipublicageo.vercel.app/api/geo';
  const GEO_TIMEOUT   = 2500;

  const CACHE_KEYS = {
    code: 'rdg_geo_region_code',
    name: 'rdg_geo_region_name',
    ts:   'rdg_geo_region_ts'
  };

  const CACHE_TTL = 1000 * 60 * 60 * 24 * 30; // 30 dias

  // Mais agressivo no primeiro add (o drawer re-renderiza e o cart.js demora um pouco)
  const RETRY_DELAYS_MS = [0, 120, 300, 700, 1200, 2000]; // ~4.3s total

  const G149 = new Set(['GO','DF','MG','SP']);
  const G179 = new Set(['RJ','ES','PR','SC','RS','MT','MS','TO']);
  const G299 = new Set(['BA','SE','PE','AL','PB','RN','CE']);

  function thresholdByUF(uf) {
    if (G149.has(uf)) return 14900;
    if (G179.has(uf)) return 17900;
    if (G299.has(uf)) return 29900;
    return 29900;
  }

  const money = c => (c / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
  const sleep = (ms) => new Promise(r => setTimeout(r, ms));

  function getCachedRegion() {
    try {
      const code = localStorage.getItem(CACHE_KEYS.code);
      const name = localStorage.getItem(CACHE_KEYS.name);
      const ts   = Number(localStorage.getItem(CACHE_KEYS.ts) || 0);

      if (!code || !name || !ts) return null;
      if (Date.now() - ts > CACHE_TTL) return null;

      return { code, name };
    } catch {
      return null;
    }
  }

  function setCachedRegion(region) {
    try {
      localStorage.setItem(CACHE_KEYS.code, region.code);
      localStorage.setItem(CACHE_KEYS.name, region.name);
      localStorage.setItem(CACHE_KEYS.ts, String(Date.now()));
    } catch {}
  }

  let regionPromise = null;

  async function fetchRegionOnce() {
    const cached = getCachedRegion();
    if (cached) return cached;

    if (regionPromise) return regionPromise;

    regionPromise = (async () => {
      const controller = new AbortController();
      const t = setTimeout(() => controller.abort(), GEO_TIMEOUT);

      try {
        const res = await fetch(GEO_ENDPOINT, {
          method: 'GET',
          credentials: 'omit',
          headers: { 'Accept': 'application/json' },
          signal: controller.signal
        });

        if (!res.ok) return null;

        const data = await res.json();
        if (!data?.region_code || !data?.region_name) return null;

        const region = {
          code: String(data.region_code).toUpperCase().trim(),
          name: String(data.region_name).trim()
        };

        setCachedRegion(region);
        return region;
      } catch {
        return null;
      } finally {
        clearTimeout(t);
      }
    })();

    return regionPromise;
  }

  async function getCartTotal() {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
      const cart = await res.json();
      return Number(cart.total_price || 0);
    } catch {
      return 0;
    }
  }

  function updateUI(nodes, region, total) {
    const uf    = region?.code || '';
    const limit = thresholdByUF(uf);

    const pct  = clamp((total / limit) * 100, 0, 100);
    const rest = Math.max(0, limit - total);

    nodes.forEach(el => {
      const nameEl = el.querySelector('[data-rdg-region-name]');
      const limitEl = el.querySelector('[data-rdg-limit]');
      const fillEl = el.querySelector('[data-rdg-fill]');
      const msgEl  = el.querySelector('[data-rdg-msg]');

      if (nameEl) nameEl.textContent = region?.name || 'Brasil';
      if (limitEl) limitEl.textContent = money(limit);
      if (fillEl) fillEl.style.width = pct + '%';

      if (msgEl) {
        msgEl.textContent =
          rest <= 0 ? 'Frete grátis liberado.' : `Faltam ${money(rest)} para frete grátis.`;
      }
    });

    // marca pra debug/garantia
    nodes.forEach(el => el.setAttribute('data-rdg-ready', '1'));
  }

  let retryInFlight = false;

  async function refreshWithRetry() {
    if (retryInFlight) return;
    retryInFlight = true;

    try {
      for (const d of RETRY_DELAYS_MS) {
        if (d) await sleep(d);

        const nodes = document.querySelectorAll(ROOT_SELECTOR);
        if (!nodes.length) continue;

        const [region, total] = await Promise.all([fetchRegionOnce(), getCartTotal()]);
        updateUI(nodes, region, total);

        // stop condition: saiu do placeholder
        const firstLimit = nodes[0].querySelector('[data-rdg-limit]')?.textContent || '';
        if (firstLimit.includes('R$') && !firstLimit.includes('--')) break;
      }
    } finally {
      retryInFlight = false;
    }
  }

  function hookGlobalObservers() {
    if (!window.MutationObserver) return;

    // Sempre que qualquer coisa do drawer/DOM mudar, tenta atualizar (dedup + retryInFlight segura custo)
    const obs = new MutationObserver(() => {
      if (document.querySelector(ROOT_SELECTOR)) refreshWithRetry();
    });

    obs.observe(document.body, { childList: true, subtree: true });
  }

  function boot() {
    refreshWithRetry();
    hookGlobalObservers();

    // Eventos do tema (se existirem)
    ['cart:update','cart:change','cart:refresh','cart-drawer:open']
      .forEach(evt => document.addEventListener(evt, refreshWithRetry));

    // Também “escuta” o add-to-cart padrão (muitos temas não disparam eventos custom)
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form && form.matches('form[action*="/cart/add"]')) {
        // dá um tempo pro Shopify atualizar o carrinho e abrir drawer
        setTimeout(refreshWithRetry, 50);
        setTimeout(refreshWithRetry, 250);
        setTimeout(refreshWithRetry, 700);
      }
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
